import { getAuthHeaders } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api"
const COUNTRY = "canada"

const handleAPIError = (error, status) => {
	const message = status === 404	
		? 'Flow options not found'
		: 'Failed to fetch flow options';
	throw new APIError(message, status);
};

export class APIError extends Error {
	constructor(message, status) {
		super(message);
		this.status = status;
	}
}

// Shared helper for consistent network error handling
const rethrowNetworkError = (error, message = 'Network error', status = 500) => {
	if (error instanceof APIError) throw error;
	throw new APIError(message, status);
};

const appendPocIdParam = (params, poc_id) => {
	if (!poc_id) return;

	if (Array.isArray(poc_id)) {
		const filteredValues = poc_id
			.map((value) => (value ?? "").toString().trim())
			.filter((value) => value.length > 0);

		if (!filteredValues.length) return;
		filteredValues.forEach(val => params.append('poc_id', val));
		return;
	}

	if (typeof poc_id === 'string' && poc_id.trim() === '') return;

	params.append('poc_id', poc_id);
};

const appendListParam = (params, key, value) => {
	if (!value) return;

	if (Array.isArray(value)) {
		const filteredValues = value
			.map((v) => (v ?? "").toString().trim())
			.filter((v) => v.length > 0);

		if (!filteredValues.length) return;
		filteredValues.forEach(val => params.append(key, val));
		return;
	}

	if (typeof value === 'string' && value.trim() === '') return;

	params.append(key, value);
};

export const fetchFlowOptions = async (flowId = 'root') => {
	try {
		const response = await fetch(`${API_BASE_URL}/flows/get-mainflow-options/${flowId}`, {
			headers: {
				...getAuthHeaders(),
			}
		})

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		throw new APIError('Network error', 500);
	}
}

export const fetchRegions = async () => {
	try {
		const response = await fetch(`${API_BASE_URL}/metadata/regions?country=${COUNTRY}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		throw new APIError('Network error', 500);
	}
}

export const fetchBrands = async () => {
	try {
		const response = await fetch(`${API_BASE_URL}/metadata/brands`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
}

export const fetchBOCampaigns = async (region, brand) => {
    try {
        const response = await fetch(`${API_BASE_URL}/campaign/get-bo-campaign-list?region=${region}&brand=${brand}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

        if (!response.ok) {
            handleAPIError(response.statusText, response.status);
        }

        const data = await response.json();
        
        // Validate response structure
        if (!data.campaigns) {
            throw new APIError('Invalid response format: missing campaigns', 500);
        }

        return data;
    } catch (error) {
        console.error("fetchBOCampaigns error:", error);
        rethrowNetworkError(error);
    }
}

export const fetchPSCampaigns = async (region,brand) => {
	try {
		const response = await fetch(`${API_BASE_URL}/campaign/get-ps-campaign-list?region=${region}&brand=${brand}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
}

export const fetchRegionalSheetnames = async (region)=>{
  try {
		const response = await fetch(`${API_BASE_URL}/budget-optimization/get-sheetnames?region=${region}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
}

export const saveFinalisedPocs = async (formData) => {
  // console.log("Saving pocs in save finalized pocs function",formData)
	try {
		const response = await fetch(`${API_BASE_URL}/budget-optimization/save-pocs`, {
			method: 'POST',
			headers: {
				...getAuthHeaders(),
			},
			body: formData
		});

		if (!response.ok) {
      handleAPIError(response.statusText, response.status);
		}
		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
}

export const saveNationalGameplanPocs = async (formData)=>{

  try{
    const response = await fetch(`${API_BASE_URL}/poc-strategy/national-gameplan/save-pocs`, {
      method: 'POST',
      headers: {
				...getAuthHeaders(),
			},
      body: formData
    });
    if (!response.ok) {
      handleAPIError(response.statusText, response.status);
		}
		
    const result = await response.json();
    return result;
  }
  catch(error){
    // console.log("Error caught in saving national gameplan pocs",error.message)
    rethrowNetworkError(error);
  }
}

export const fetchObjectives = async (region,brand,date) => {
	try {
		const response = await fetch(`${API_BASE_URL}/poc-strategy/get-objectives?region=${region}&brand=${brand}&date=${date}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
}


export const fetchPOCStrategyFilterOptions = async () =>{
	try {
		const response = await fetch(`${API_BASE_URL}/poc-strategy/chart-options`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
}


export const fetPocStrategyChartData = async (x_axis, y_axis, objective_name, brand, zoolander, keyAccount) => {
	// console.log("objective_name is ", objective_name);
	try {
		const params = new URLSearchParams();
		params.append('x_axis', x_axis);
		params.append('y_axis', y_axis);
		params.append('objective_name', objective_name);
		params.append('brand', brand);

		if (zoolander && zoolander !== "All") {
			params.append('zoolander', zoolander);
		}

		if (keyAccount && keyAccount !== "All") {
			params.append('key_account', keyAccount);
		}

		const response = await fetch(`${API_BASE_URL}/poc-strategy/chart-data?${params.toString()}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
}

export const fetchPocMetadata = async (region, brand) => {
	try {
		const response = await fetch(`${API_BASE_URL}/metadata/poc-metadata`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		rethrowNetworkError(error);
	}
};


export async function fetchSummaryMetrics(region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account) { 
    const params = new URLSearchParams(); 
    if (region) params.append('region', region); 
    if (brand) params.append('brand', brand); 
    appendListParam(params, 'campaign_name', campaignName); 
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);
    appendPocIdParam(params, poc_id);
    appendListParam(params, 'poc_name', poc_name);
    if (key_account) params.append('key_account', key_account);
    const endpoint = `${API_BASE_URL}/post-analysis/summary?${params.toString()}`; 
    try { 
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
            }
        })
        if (!response.ok) { 
            handleAPIError(response.statusText, response.status); 
        } 
        return await response.json();
    } catch (error) { 
        rethrowNetworkError(error); 
    } 
}

export const fetchSummaryChart = async (xAxis, yAxis, region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account) => {
  try {
    const params = new URLSearchParams();
    params.append('x_axis', xAxis.toLowerCase());
    params.append('y_axis', yAxis.toLowerCase());
    if (region) params.append('region', region);
    if (brand) params.append('brand', brand);
    appendListParam(params, 'campaign_name', campaignName);
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);
    appendPocIdParam(params, poc_id);
    appendListParam(params, 'poc_name', poc_name);
    if (key_account) params.append('key_account', key_account);

    const response = await fetch(`${API_BASE_URL}/post-analysis/summary-chart?${params.toString()}`, {
      headers: {
        ...getAuthHeaders(),
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to fetch summary chart data');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export async function fetchSummaryTable(region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account, page = 1, page_size = 20) {
    const params = new URLSearchParams();
    if (region) params.append('region', region);
    if (brand) params.append('brand', brand);
    appendListParam(params, 'campaign_name', campaignName);
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);
    appendPocIdParam(params, poc_id);
    appendListParam(params, 'poc_name', poc_name);
    if (key_account) params.append('key_account', key_account);
    params.append('page', page);
    params.append('page_size', page_size);
    const endpoint = `${API_BASE_URL}/post-analysis/summary-table?${params.toString()}`;

    try {
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
            }
        });
        if (!response.ok) {
            handleAPIError(response.statusText, response.status);
        }
        return await response.json();
    } catch (error) {
        rethrowNetworkError(error);
    }
}

export async function fetchMetricOptions(context = "summary") {
    const endpoint = `${API_BASE_URL}/post-analysis/metric-options?context=${context}`;

    try {
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
            }
        });

        if (!response.ok) {
            handleAPIError(response.statusText, response.status);
        }

        return await response.json();
    } catch (error) {
        rethrowNetworkError(error);
    }
}

export async function fetchFilterOptions(region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account) {
    const params = new URLSearchParams();
    if (region) params.append('region', region);
    if (brand) params.append('brand', brand);
    appendListParam(params, 'campaign_name', campaignName);
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);
    appendPocIdParam(params, poc_id);
    appendListParam(params, 'poc_name', poc_name);
    if (key_account) params.append('key_account', key_account);

    const endpoint = `${API_BASE_URL}/post-analysis/get-filters?${params.toString()}`;

    try {
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
            }
        });

        if (!response.ok) {
            handleAPIError(response.statusText, response.status);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        rethrowNetworkError(error);
    }
}

export async function fetchDetailedInsightsView({ metric, brand, region, campaign_name, start_date, end_date, poc_id, poc_name, key_account }) {
  const params = new URLSearchParams();
  if (metric) params.append('metric', metric);
  if (brand) params.append('brand', brand);
  if (region) params.append('region', region);
  appendListParam(params, 'campaign_name', campaign_name);
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  appendPocIdParam(params, poc_id);
  appendListParam(params, 'poc_name', poc_name);
  if (key_account) params.append('key_account', key_account);
  const endpoint = `${API_BASE_URL}/post-analysis/table-chart-view?${params.toString()}`;
  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      }
    });
    if (!response.ok) {
      handleAPIError(response.statusText, response.status);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    rethrowNetworkError(error);
  }
}

export async function fetchBenchmarksData({ region, brand, campaign_name, start_date, end_date, poc_id, poc_name, key_account, page = 1, page_size = 20 }) {
  const params = new URLSearchParams();
  if (region) params.append('region', region);
  if (brand) params.append('brand', brand);
  appendListParam(params, 'campaign_name', campaign_name);
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  appendPocIdParam(params, poc_id);
  appendListParam(params, 'poc_name', poc_name);
  if (key_account) params.append('key_account', key_account);
  params.append('page', page);
  params.append('page_size', page_size);

  const endpoint = `${API_BASE_URL}/post-analysis/benchmarks?${params.toString()}`;

  try {
    // console.log('Fetching benchmarks data from:', endpoint);
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.detail || 'Failed to fetch benchmark data',
        response.status
      );
    }

    const data = await response.json();
    // console.log('Received benchmarks data:', data);
    
    // Ensure we have the required data structure
    if (!data.metrics || !data.brand_chart || !data.region_chart || !data.deviation_table) {
      throw new APIError('Invalid data structure received from server', 500);
    }

    return {
      metrics: data.metrics,
      brand_chart: data.brand_chart,
      region_chart: data.region_chart,
      deviation_table: data.deviation_table,
      deviation_table_total_rows: data.deviation_table_total_rows || 0
    };
  } catch (error) {
    console.error('Error in fetchBenchmarksData:', error);
    rethrowNetworkError(error);
  }
}

export async function fetchROIData(params) {
    let endpoint = `${API_BASE_URL}/post-analysis/roi`;
    if (params && params.toString()) {
        endpoint += `?${params.toString()}`;
    }
    try {
        // console.log('Fetching ROI data from:', endpoint);
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...getAuthHeaders(),
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        // console.log('Received ROI data:', data);
        
        if (!data) {
            throw new Error('Invalid response format: missing data property');
        }
        
        return data;
    } catch (error) {
        console.error('Error in fetchROIData:', error);
        throw error;
    }
} 

export async function saveAdhocRequest(formDataToSend){
	try{

		// console.log("Raising a request in saveAdhoc Request",formDataToSend )
		const response = await fetch(`${API_BASE_URL}/adhoc-request/save-data`, {
			method: 'POST',
			headers: {
				...getAuthHeaders(),
			},
			body: formDataToSend,
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.detail || 'Failed to save adhoc request');
		}

		const result = await response.json();
		return result;
	}
	catch(error){
		rethrowNetworkError(error);
	}
}

export async function fetchPocsAdhocRequest(zoolander, keyAccount){

	try{
		// console.log("zoolander and key account is ", zoolander,keyAccount)
		const params = new URLSearchParams();
		if (zoolander) params.append('zoolander', zoolander);
		if (keyAccount) params.append('key_account', keyAccount);


		const response = await fetch(`${API_BASE_URL}/adhoc-request/get-pocs?${params.toString()}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.detail || 'Failed to save adhoc request');
		}

		const result = await response.json();
		return result;
	}
	catch(error){
		rethrowNetworkError(error);
	} 
}

// ============================================
// SPA Authentication API Functions (MSAL Flow)
// ============================================

/**
 * Fetches MSAL configuration from backend
 * @returns {Promise<{client_id: string, authority: string, redirect_uri: string, scopes: string[]}>}
 */
export async function fetchAuthConfig() {
	try {
		const response = await fetch(`${API_BASE_URL}/auth/config`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			}
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new APIError(errorData.detail || 'Failed to fetch auth config', response.status);
		}

		return await response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error fetching auth config', 500);
	}
}

/**
 * Exchanges Microsoft access token for backend JWT
 * @param {string} microsoftAccessToken - Access token from MSAL
 * @returns {Promise<{access_token: string, expires_in: number, user: object}>}
 */
export async function exchangeToken(microsoftAccessToken) {
	try {
		const response = await fetch(`${API_BASE_URL}/auth/exchange`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ access_token: microsoftAccessToken })
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new APIError(errorData.detail || 'Token exchange failed', response.status);
		}

		return await response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error during token exchange', 500);
	}
}

/**
 * Fetches the current user's profile
 * @param {string} token - Backend JWT access token
 * @returns {Promise<object>} User profile data
 */
export async function fetchUserProfile(token) {
	try {
		const response = await fetch(`${API_BASE_URL}/auth/me`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`
			}
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new APIError(errorData.detail || 'Failed to fetch user profile', response.status);
		}

		return await response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error fetching profile', 500);
	}
}

/**
 * Validates the current JWT token
 * @param {string} token - Backend JWT access token
 * @returns {Promise<{valid: boolean, user?: object}>}
 */
export async function validateToken(token) {
	try {
		const response = await fetch(`${API_BASE_URL}/auth/validate`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`
			}
		});

		if (!response.ok) {
			return { valid: false };
		}

		return await response.json();
	} catch (error) {
		return { valid: false };
	}
}

/**
 * Logs out the current user
 * @param {string} token - Backend JWT access token
 * @returns {Promise<{message: string, user_email: string}>}
 */
export async function logoutUser(token) {
	try {
		const response = await fetch(`${API_BASE_URL}/auth/logout`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`
			}
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new APIError(errorData.detail || 'Logout failed', response.status);
		}

		return await response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error during logout', 500);
	}
}

// /**
//  * Logs out the current user
//  * @param {string} token - Backend JWT access token
//  * @returns {Promise<{message: string, user_email: string}>}
//  */
// export async function logoutUser(token) {
// 	try {
// 		const response = await fetch(`${API_BASE_URL}/auth/logout`, {
// 			method: 'POST',
// 			headers: {
// 				'Content-Type': 'application/json',
// 				'Authorization': `Bearer ${token}`
// 			}
// 		});

// 		if (!response.ok) {
// 			const errorData = await response.json().catch(() => ({}));
// 			throw new APIError(errorData.detail || 'Logout failed', response.status);
// 		}

// 		return await response.json();
// 	} catch (error) {
// 		if (error instanceof APIError) throw error;
// 		throw new APIError('Network error during logout', 500);
// 	}
// }

// Campaign API functions
export const fetchCampaignOptions = async () => {
	try {
		const response = await fetch(`${API_BASE_URL}/campaign/campaign-options`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const fetchCampaigns = async (region, brand, bdrEmail = null) => {
	try {
		if (!region || !brand) {
			throw new APIError('Region and brand are required', 400);
		}

		// Build URL with query parameters
		const params = new URLSearchParams();
		params.append('region', region);
		params.append('brand', brand);
		
		// Add bdr_email if provided
		if (bdrEmail) {
			params.append('bdr_email', bdrEmail);
		}

		const url = `${API_BASE_URL}/campaign/get-campaigns-to-edit?${params.toString()}`;
		const response = await fetch(url, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const fetchCampaignById = async (campaignId) => {
	try {
		const response = await fetch(`${API_BASE_URL}/campaign/campaign/${campaignId}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

// export const fetchCampaignById = async (campaignId) => {
// 	try {
// 		const response = await fetch(`${API_BASE_URL}/campaign/campaign/${campaignId}`);

// 		if (!response.ok) {
// 			handleAPIError(response.statusText, response.status);
// 		}

// 		return response.json();
// 	} catch (error) {
// 		if (error instanceof APIError) throw error;
// 		throw new APIError('Network error', 500);
// 	}
// };
export const fetchAmplifyCampaignName = async (region, brand, campaignName, budget_type) => {
	try {
		const params = new URLSearchParams();
		if (region) params.append('region', region);
		if (brand) params.append('brand', brand);
		if (campaignName) params.append('campaign_name', campaignName);
		if (budget_type) params.append('budget_type', budget_type);

		const response = await fetch(`${API_BASE_URL}/campaign/get-amplify-campaign-name?${params.toString()}`, {
			headers: {
				...getAuthHeaders(),
			}
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};


export const createCampaign = async (campaignData) => {
	try {
		// console.log('createCampaign - Raw data received:', campaignData);
		// console.log('createCampaign - Data type:', typeof campaignData);
		
		const requestBody = JSON.stringify(campaignData);
		// console.log('createCampaign - Stringified body:', requestBody);
		
		const response = await fetch(`${API_BASE_URL}/campaign/new-campaign`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
			body: requestBody,
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const detail = errorData.detail || errorData.message;
			const message = typeof detail === 'string'
				? detail
				: Array.isArray(detail)
					? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
					: `HTTP ${response.status}: ${response.statusText}`;
			throw new APIError(message, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const updateCampaign = async (campaignId, campaignData) => {
	try {
		// console.log('updateCampaign - Campaign ID:', campaignId);
		// console.log('updateCampaign - Raw data received:', campaignData);
		
		const requestBody = JSON.stringify(campaignData);
		// console.log('updateCampaign - Stringified body:', requestBody);
		
		const response = await fetch(`${API_BASE_URL}/campaign/campaign/${campaignId}`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
			body: requestBody,
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const detail = errorData.detail || errorData.message;
			const message = typeof detail === 'string'
				? detail
				: Array.isArray(detail)
					? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
					: `HTTP ${response.status}: ${response.statusText}`;
			throw new APIError(message, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const uploadCampaignCreatives = async (campaignId, formData) => {
	try {
		// console.log(`API call: POST ${API_BASE_URL}/campaign/campaign-creatives/${campaignId}`);
		// console.log('Data being sent:', formData);
		// console.log('Data type:', formData.constructor.name);
		// console.log('Is FormData?', formData instanceof FormData);
		
		// Validate that we're actually sending FormData
		if (!(formData instanceof FormData)) {
			console.error('ERROR: Expected FormData but received:', typeof formData);
			throw new APIError('Invalid data type: Expected FormData', 400);
		}
		
		// Debug: Log FormData contents in API service
		// console.log('API Service - FormData contents:');
		for (let [key, value] of formData.entries()) {
			// console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
		}
		
		// Ensure we're sending FormData and not JSON
		// console.log('Final check before sending:');
		// console.log('- Body is FormData:', formData instanceof FormData);
		// console.log('- Body constructor:', formData.constructor.name);
		
		// API expects FormData for all creative types as per specification
		const response = await fetch(`${API_BASE_URL}/campaign/campaign-creatives/${campaignId}`, {
			method: 'POST',
			headers: {
				...getAuthHeaders(),
			},
			body: formData, // FormData - browser will automatically set Content-Type: multipart/form-data
			// CRITICAL: Do not set Content-Type header - let browser set it with boundary
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const detail = errorData.detail || errorData.message;
			const message = typeof detail === 'string'
				? detail
				: Array.isArray(detail)
					? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
					: `HTTP ${response.status}: ${response.statusText}`;
			throw new APIError(message, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const updateCampaignCreatives = async (campaignId, formData) => {
	try {
		
		// Validate that we're actually sending FormData
		if (!(formData instanceof FormData)) {
			console.error('ERROR: Expected FormData but received:', typeof formData);
			throw new APIError('Invalid data type: Expected FormData', 400);
		}
		
		// Debug: Log FormData contents in API service
		for (let [key, value] of formData.entries()) {
			// console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
		}
		
		// API expects FormData for updating creatives
		const response = await fetch(`${API_BASE_URL}/campaign/campaign-creatives/${campaignId}`, {
			method: 'PUT',
			headers: {
				...getAuthHeaders(),
			},
			body: formData, // FormData - browser will automatically set Content-Type: multipart/form-data
			// CRITICAL: Do not set Content-Type header - let browser set it with boundary
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const detail = errorData.detail || errorData.message;
			const message = typeof detail === 'string'
				? detail
				: Array.isArray(detail)
					? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
					: `HTTP ${response.status}: ${response.statusText}`;
			throw new APIError(message, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const addCampaignPocs = async (campaignId, pocItems) => {
	try {
		// API expects array of POC items directly
		const response = await fetch(`${API_BASE_URL}/campaign/campaign-pocs/${campaignId}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
			body: JSON.stringify(pocItems),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const detail = errorData.detail || errorData.message;
			const message = typeof detail === 'string'
				? detail
				: Array.isArray(detail)
					? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
					: `HTTP ${response.status}: ${response.statusText}`;
			throw new APIError(message, response.status);
		}

		return response.json();
	} catch (error) {
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const fetchCampaignPocs = async (campaignId, skip = 0, limit = 100) => {
	if (!campaignId) {
		throw new APIError('Campaign ID is required', 400);
	}

	try {
		const params = new URLSearchParams({
			skip: skip.toString(),
			limit: limit.toString()
		});

		const response = await fetch(`${API_BASE_URL}/campaign/campaign-pocs/${campaignId}?${params}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const detail = errorData.detail || errorData.message;
			const message = typeof detail === 'string'
				? detail
				: Array.isArray(detail)
					? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
					: `HTTP ${response.status}: ${response.statusText}`;
			throw new APIError(message, response.status);
		}

		const result = await response.json();

		if (!result.status) {
			throw new APIError(result.message || 'Failed to fetch campaign POCs', 500);
		}

		return result;
	} catch (error) {
		console.error('Error fetching campaign POCs:', error);
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};



// POC API functions
export const fetchPOCsByRegion = async (region, skip = 0, limit = 1000) => {
	if (!region) {
		throw new APIError('Region is required', 400);
	}

	try {
		const params = new URLSearchParams({
			region: region,
			skip: skip.toString(),
			limit: limit.toString()
		});

		const response = await fetch(`${API_BASE_URL}/poc/pocs?${params}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		const result = await response.json();

		if (!result.status) {
			throw new APIError(result.message || 'Failed to fetch POCs', 500);
		}

		return result;
	} catch (error) {
		console.error('Error fetching POCs by region:', error);
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const searchPOCs = async (query, region = null, skip = 0, limit = 100) => {
	if (!query || query.trim().length < 2) {
		throw new APIError('Search query must be at least 2 characters', 400);
	}

	if (!region) {
		throw new APIError('Region is required for POC search', 400);
	}

	try {
		const params = new URLSearchParams({
			q: query.trim(),
			region: region,
			skip: skip.toString(),
			limit: limit.toString()
		});

		const response = await fetch(`${API_BASE_URL}/poc/search?${params}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
		});

		if (!response.ok) {
			handleAPIError(response.statusText, response.status);
		}

		const result = await response.json();

		if (!result.status) {
			throw new APIError(result.message || 'Failed to search POCs', 500);
		}

		return result;
	} catch (error) {
		console.error('Error searching POCs:', error);
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

export const uploadPOCFile = async (file) => {
	if (!file) {
		throw new APIError('File is required', 400);
	}

	// Check file type
	const fileExtension = file.name.split('.').pop().toLowerCase();
	const supportedTypes = ['xlsx', 'xls', 'csv'];
	
	if (!supportedTypes.includes(fileExtension)) {
		throw new APIError(`Unsupported file type: ${fileExtension}. Please upload Excel (.xlsx, .xls) or CSV (.csv) files only.`, 400);
	}

	try {
		const formData = new FormData();
		formData.append('file', file);

		// Use the same endpoint - backend should handle both Excel and CSV
		const response = await fetch(`${API_BASE_URL}/poc/upload-file`, {
			method: 'POST',
			headers: {
				...getAuthHeaders(),
			},
			body: formData
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new APIError(
				errorData.detail || `Failed to process ${file.name}: ${response.statusText}`,
				response.status
			);
		}

		const result = await response.json();

		if (!result.status) {
			throw new APIError(result.message || 'Failed to upload file', 500);
		}

		return result;
	} catch (error) {
		console.error('Error uploading POC file:', error);
		if (error instanceof APIError) throw error;
		throw new APIError('Network error', 500);
	}
};

// Keep the old function name for backward compatibility
export const uploadExcelPOCs = uploadPOCFile;

export const fetchOptimizedBudgets = async (pocList) => {
  // pocList: Array of { poc_id, poc_name }
  try {
    const response = await fetch(`${API_BASE_URL}/campaign/get-optimized-budgets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(pocList),
    });
    if (!response.ok) {
      handleAPIError(response.statusText, response.status);
    }
    return response.json();
  } catch (error) {
    throw new APIError('Network error', 500);
  }
};

// ============================================
// Business Admin API Functions
// ============================================

// Get dashboard overview
export const getBusinessAdminOverview = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.brand) params.append('brand', filters.brand);
    if (filters.region) params.append('region', filters.region);
    if (filters.period) params.append('period', filters.period);

    const response = await fetch(`${API_BASE_URL}/business-admin/overview?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get summary metrics
export const getBusinessAdminSummary = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.brand) params.append('brand', filters.brand);
    if (filters.region) params.append('region', filters.region);
    if (filters.period) params.append('period', filters.period);

    const response = await fetch(`${API_BASE_URL}/business-admin/summary?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get recent campaigns
export const getBusinessAdminCampaigns = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.brand) queryParams.append('brand', params.brand);
    if (params.region) queryParams.append('region', params.region);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);

    const response = await fetch(`${API_BASE_URL}/business-admin/recent-campaigns?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get campaign analytics
export const getBusinessAdminAnalytics = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.brand) params.append('brand', filters.brand);
    if (filters.region) params.append('region', filters.region);
    if (filters.period) params.append('period', filters.period);

    const response = await fetch(`${API_BASE_URL}/business-admin/analytics?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get budget analytics
export const getBusinessAdminBudgetAnalytics = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.brand) params.append('brand', filters.brand);
    if (filters.region) params.append('region', filters.region);
    if (filters.period) params.append('period', filters.period);

    const response = await fetch(`${API_BASE_URL}/business-admin/budget-analytics?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get campaign detail
export const getBusinessAdminCampaignDetail = async (campaignId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/business-admin/campaign/${campaignId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Approve campaign
export const approveCampaign = async (campaignId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/business-admin/campaign/${campaignId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Reject campaign
export const rejectCampaign = async (campaignId, reason = '') => {
  try {
    const queryParams = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await fetch(`${API_BASE_URL}/business-admin/campaign/${campaignId}/reject${queryParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Search campaigns
export const searchBusinessAdminCampaigns = async (searchParams = {}) => {
  try {
    const params = new URLSearchParams();
    if (searchParams.q) params.append('q', searchParams.q);
    if (searchParams.brand) params.append('brand', searchParams.brand);
    if (searchParams.region) params.append('region', searchParams.region);
    if (searchParams.status) params.append('status', searchParams.status);
    if (searchParams.page) params.append('page', searchParams.page);
    if (searchParams.per_page) params.append('per_page', searchParams.per_page);

    const response = await fetch(`${API_BASE_URL}/business-admin/search?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get filter options
export const getBusinessAdminFilterOptions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/business-admin/filter-options`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get campaigns by status
export const getBusinessAdminCampaignsByStatus = async (status, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.brand) queryParams.append('brand', params.brand);
    if (params.region) queryParams.append('region', params.region);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);

    const response = await fetch(`${API_BASE_URL}/business-admin/campaigns-by-status/${status}?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Get pending approvals
export const getBusinessAdminPendingApprovals = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);

    const response = await fetch(`${API_BASE_URL}/business-admin/pending-approvals?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Download creative media file (image/video) via backend – works in prod where files live on API server
export const downloadCreativeMedia = async (filePath) => {
  const url = `${API_BASE_URL}/business-admin/download/media?file_path=${encodeURIComponent(filePath)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { ...getAuthHeaders() },
    credentials: 'include',
  });
  return response;
};

// Download POCs for a campaign
export const downloadCampaignPOCs = async (campaignId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/business-admin/campaign/${campaignId}/download-pocs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Update campaign status
export const updateCampaignStatus = async (campaignId, status, notes = '') => {
  try {
    const response = await fetch(`${API_BASE_URL}/business-admin/campaign/${campaignId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({ status, notes })
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Edit campaign details (business admin)
export const editCampaignDetails = async (campaignId, campaignData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/business-admin/campaign/${campaignId}/edit`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(campaignData)
    });

    if (!response.ok) {
      throw new APIError(`HTTP error! status: ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};

// Delete campaign (business admin)
export const deleteCampaign = async (campaignId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/business-admin/campaign/${campaignId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || errorData.detail || `Failed to delete campaign: ${response.statusText}`,
        response.status
      );
    }

    return response.json();
  } catch (error) {
    console.error('Error deleting campaign:', error);
    if (error instanceof APIError) throw error;
    throw new APIError('Network error', 500);
  }
};
